import React from "react";
import PropTypes from "prop-types";
import { styled, useTheme } from "@mui/material/styles";

import MuiStep from "@mui/material/Step";
import MuiStepper from "@mui/material/Stepper";
import MuiStepLabel from "@mui/material/StepLabel";
import MuiStepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";

import DoneIcon from "assets/svg/done.svg";
import EllipseIcon from "assets/svg/ellipse.svg";

const StepIcon = ({ completed, active, icon }) => {
  const { palette } = useTheme();
  if (completed)
    return (
      <StepIconCompletedContainer>
        <DoneIcon width={24} color={palette.tertiary.onTertiary} />
      </StepIconCompletedContainer>
    );
  if (active)
    return (
      <StepIconActiveContainer>
        <EllipseIcon width={24} color={palette.primary.main} />
      </StepIconActiveContainer>
    );
  return <StepIconContainer>{icon}</StepIconContainer>;
};

const StepIconCompletedContainer = styled("div")(({ theme }) => ({
  display: "inline-flex",
  padding: "8px",
  alignItems: "center",
  gap: "8px",
  borderRadius: "100px",
  background: theme.palette.tertiary.tertiary,
}));

const StepIconActiveContainer = styled("div")(({ theme }) => ({
  display: "flex",
  width: "40px",
  height: "40px",
  padding: "8px",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "100px",
  border: "1px solid " + theme.palette.primary.main,
}));

const StepIconContainer = styled("div")(({ theme }) => ({
  display: "flex",
  width: "40px",
  height: "40px",
  padding: "8px",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "100px",
  border: "1px solid " + theme.palette.outline.outline,
}));

const Connector = styled(MuiStepConnector)(({ theme }) => ({
  marginLeft: "19px",
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: theme.palette.primary.main,
      borderBottom: "30px solid " + theme.palette.secondary.secondaryContainer,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: theme.palette.tertiary.tertiary,
    },
  },
  [`&.${stepConnectorClasses.disabled}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      background: theme.palette.secondary.secondaryContainer,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    width: "2px",
    height: "60px",
    border: "0",
  },
}));

const Stepper = (props) => {
  const { steps, ...otherProps } = props;

  const renderStep = ({ title }, index) => {
    const label = `Step ${index + 1}`;
    return (
      <MuiStep key={index}>
        <MuiStepLabel StepIconComponent={StepIcon} optional={title}>
          {label}
        </MuiStepLabel>
      </MuiStep>
    );
  };

  return (
    <MuiStepper connector={<Connector />} {...otherProps}>
      {steps.map(renderStep)}
    </MuiStepper>
  );
};

StepIcon.propTypes = {
  completed: PropTypes.bool.isRequired,
  active: PropTypes.bool.isRequired,
  icon: PropTypes.number.isRequired,
};

Stepper.propTypes = {
  activeStep: PropTypes.number.isRequired,
  orientation: PropTypes.string,
  steps: PropTypes.array.isRequired,
};

export default Stepper;
